import { useTranslation } from 'react-i18next';
import { FreezeFormErrors, FreezeFormValues } from '../CreateFreeze/CreateFreeze.types';
import styles from './Steps.module.css';
import { ChangeEvent, useEffect, useState } from 'react';
import { InputStates } from '@/components/Input/Input.types';
import RadioCard from '@/components/RadioCard/RadioCard';
import Tooltip from '@/components/Tooltip/Tooltip';
import { PositionTypes } from '@/components/Tooltip/Tooltip.types';
import Input from '@/components/Input/Input';
import { Grid, Container } from '@mui/material';
import { NationalProgramStates } from '@/types/nationalProgram.types';

function nothingChanges(editing: boolean | undefined, initialFormData: FreezeFormValues, formValues: FreezeFormValues) {
  if (
    initialFormData.freezeName === formValues.freezeName &&
    formValues.freezeName === undefined &&
    initialFormData.optInStartDate === formValues.optInStartDate &&
    formValues.optInStartDate === undefined &&
    initialFormData.freezeStartDate === formValues.freezeStartDate &&
    formValues.freezeStartDate === undefined &&
    initialFormData.freezeEndDate === formValues.freezeEndDate &&
    formValues.freezeEndDate === undefined
  ) {
    return true;
  }
  return false;
}
export const FreezeStepOne = ({
  initialFormData,
  state,
  editing,
  onChangeFreezeData,
}: {
  initialFormData: FreezeFormValues;
  editing?: boolean;
  state?: NationalProgramStates;
  onChangeFreezeData: (data: FreezeFormValues, valid: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<FreezeFormValues>(initialFormData);
  const [formErrors, setFormErrors] = useState<FreezeFormErrors>({} as FreezeFormErrors);

  const dateTomorrow = new Date();
  dateTomorrow.setDate(dateTomorrow.getDate() + 1);
  const truncatedTomorrow = dateTomorrow.toISOString().slice(0, 10);

  const isValid = () => {
    const needToValidateOptInDate = !(
      editing &&
      (state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN)
    );
    const needToValidateStartDate = !(editing && state === NationalProgramStates.LIVE);
    let error = false;

    // invalid but without messages
    if (!editing && nothingChanges(editing, initialFormData, formValues)) {
      return false;
    }
    const errorMessages = {
      freezeName: '',
      optInStartDate: '',
      freezeStartDate: '',
      freezeEndDate: '',
      defaultPrice: '',
      nationalPricePointTwo: '',
      nationalPricePointThree: '',
      nationalPricePointFour: '',
      optOutHidden: '',
      redeemableOnline: '',
      visibleOnline: '',
    };
    if (!formValues.freezeName || formValues.freezeName.length > 50) {
      errorMessages.freezeName = t('error.freezeNameLength');
      error = true;
    }
    if (formValues.freezeType === 'NATIONAL_PROGRAM') {
      if (needToValidateOptInDate && (!formValues.optInStartDate || formValues.optInStartDate < truncatedTomorrow)) {
        errorMessages.optInStartDate = t('error.freezeOptInStartDate');
        error = true;
      }
    }
    if (needToValidateStartDate && (!formValues.freezeStartDate || formValues.freezeStartDate < truncatedTomorrow)) {
      errorMessages.freezeStartDate = t('error.freezeStartDate');
      error = true;
    }
    if (formValues.freezeType === 'NATIONAL_PROGRAM') {
      if (formValues.freezeStartDate <= formValues.optInStartDate) {
        errorMessages.freezeStartDate = t('error.freezeStartDateAfterOptIn');
        error = true;
      }
    }
    if (!formValues.freezeEndDate || formValues.freezeEndDate <= formValues.freezeStartDate) {
      errorMessages.freezeEndDate = t('error.freezeEndDate');
      error = true;
    }

    setFormErrors(errorMessages);
    const invalid =
      formValues.freezeType === 'MAINTENANCE'
        ? formValues.freezeName === '' || formValues.freezeStartDate === '' || formValues.freezeEndDate === ''
        : formValues.freezeName === '' ||
          formValues.optInStartDate === '' ||
          formValues.freezeStartDate === '' ||
          formValues.freezeEndDate === '';
    const returnValue = !error && !invalid;
    return returnValue;
  };

  useEffect(() => {
    const formValid = isValid();
    onChangeFreezeData(formValues, formValid);
  }, [formValues]);

  const handleFreezeStartDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      freezeStartDate: event.target.value,
      freezeEndDate: '',
    });
    setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleFreezeTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      freezeType: event.target.id,
    });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleOptInStartDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      optInStartDate: event.target.value,
      freezeStartDate: '',
      freezeEndDate: '',
    });
    setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  function canShowMaintenanceForm() {
    return (editing && formValues.freezeType === 'MAINTENANCE') || !!editing === false;
  }
  function canShowNationalProgramForm() {
    return (editing && formValues.freezeType === 'NATIONAL_PROGRAM') || !!editing === false;
  }

  return (
    <Container fixed>
      <Grid container direction="column" justifyContent="center" alignItems="center" spacing={4} minWidth="720px">
        <div className={styles.accordionContainer}>
          {canShowMaintenanceForm() && (
            <RadioCard
              id="MAINTENANCE"
              name="freeze"
              title={
                <div className={styles.accordionTitle}>
                  <div>{t('createFreezeModal.stepOne.maintenance.header')}</div>
                  <Tooltip position={PositionTypes.Top} content={t('createFreezeModal.stepOne.maintenance.tooltip')} />
                </div>
              }
              checked={formValues.freezeType === 'MAINTENANCE'}
              onChange={handleFreezeTypeChange}
            >
              <div className={styles.divider} />
              <div className={styles.widgetBody}>
                <h3>{t('createFreezeModal.stepOne.maintenance.formHeader')}</h3>
                <div className={styles.inputContainer}>
                  <Input
                    id="freezeName"
                    label={t('createFreezeModal.stepOne.maintenance.formName')}
                    value={formValues.freezeName}
                    onChange={handleInputChange}
                    subText={formErrors.freezeName}
                    state={!formErrors.freezeName ? InputStates.Default : InputStates.Error}
                    disabled={editing && state === NationalProgramStates.LIVE}
                  />
                </div>

                <Grid container direction="row" spacing={3}>
                  <Grid item xs={6}>
                    <div className={styles.inputContainerFull}>
                      <Input
                        type="date"
                        id="freezeStartDate"
                        label={t('createFreezeModal.stepOne.maintenance.formStartDate')}
                        value={formValues.freezeStartDate?.slice(0, 10)}
                        onChange={handleFreezeStartDateChange}
                        min={truncatedTomorrow}
                        data-testid="freeze-start-date"
                        subText={formErrors.freezeStartDate}
                        state={!formErrors.freezeStartDate ? InputStates.Default : InputStates.Error}
                        disabled={editing && state === NationalProgramStates.LIVE}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className={styles.inputContainerFull}>
                      <Input
                        type="date"
                        id="freezeEndDate"
                        label={t('createFreezeModal.stepOne.maintenance.formEndDate')}
                        value={formValues.freezeEndDate?.slice(0, 10)}
                        onChange={handleInputChange}
                        min={formValues.freezeStartDate?.slice(0, 10)}
                        data-testid="freeze-end-date"
                        subText={formErrors.freezeEndDate}
                        state={!formErrors.freezeEndDate ? InputStates.Default : InputStates.Error}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
            </RadioCard>
          )}
          {canShowNationalProgramForm() && (
            <RadioCard
              id="NATIONAL_PROGRAM"
              name="freeze"
              title={
                <div className={styles.accordionTitle}>
                  <div>{t('createFreezeModal.stepOne.nationalProgram.header')}</div>
                  <Tooltip
                    position={PositionTypes.Top}
                    content={t('createFreezeModal.stepOne.nationalProgram.tooltip')}
                  />
                </div>
              }
              checked={formValues.freezeType === 'NATIONAL_PROGRAM'}
              onChange={handleFreezeTypeChange}
            >
              <div className={styles.divider} />
              <div className={styles.widgetBody}>
                <h3>{t('createFreezeModal.stepOne.nationalProgram.formHeader')}</h3>
                <Grid container direction="row" spacing={3}>
                  <Grid item xs={6}>
                    <div className={styles.inputContainerFull}>
                      <Input
                        id="freezeName"
                        label={t('createFreezeModal.stepOne.nationalProgram.formName')}
                        value={formValues.freezeName}
                        onChange={handleInputChange}
                        subText={formErrors.freezeName}
                        state={!formErrors.freezeName ? InputStates.Default : InputStates.Error}
                        disabled={
                          editing && (state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN)
                        }
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid container direction="row" spacing={3}>
                  <Grid item xs={6}>
                    <div className={styles.inputContainerFull}>
                      <div className={styles.rowWithTooltip}>
                        <h3>{t('createFreezeModal.stepOne.nationalProgram.formOptInHeader')}</h3>
                        <Tooltip
                          position={PositionTypes.Top}
                          content={t('createFreezeModal.stepOne.nationalProgram.formOptInTooltip')}
                        />
                      </div>

                      <Input
                        type="date"
                        id="optInStartDate"
                        label={t('createFreezeModal.stepOne.nationalProgram.formOptInStart')}
                        value={formValues.optInStartDate?.slice(0, 10)}
                        onChange={handleOptInStartDateChange}
                        min={truncatedTomorrow}
                        data-testid="opt-in-start-date"
                        subText={formErrors.optInStartDate}
                        state={!formErrors.optInStartDate ? InputStates.Default : InputStates.Error}
                        disabled={
                          editing && (state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN)
                        }
                      />
                    </div>
                  </Grid>
                </Grid>
                <h3>{t('createFreezeModal.stepOne.nationalProgram.formFreezePeriodHeader')}</h3>
                <Grid container direction="row" spacing={3}>
                  <Grid item xs={6}>
                    {/* Freeze start date must be after opt-in period */}
                    <div className={styles.inputContainerFull}>
                      <Input
                        type="date"
                        id="freezeStartDate"
                        label={t('createFreezeModal.stepOne.nationalProgram.formStartDate')}
                        value={formValues?.freezeStartDate?.slice(0, 10)}
                        onChange={handleFreezeStartDateChange}
                        min={formValues?.optInStartDate?.slice(0, 10)}
                        data-testid="freeze-start-date"
                        subText={formErrors.freezeStartDate}
                        disabled={editing && state === NationalProgramStates.LIVE}
                        state={!formErrors.freezeStartDate ? InputStates.Default : InputStates.Error}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    {/* Freeze end date must be after freeze start date */}
                    <div className={styles.inputContainerFull}>
                      <Input
                        type="date"
                        id="freezeEndDate"
                        label={t('createFreezeModal.stepOne.nationalProgram.formEndDate')}
                        value={formValues?.freezeEndDate?.slice(0, 10)}
                        onChange={handleInputChange}
                        min={formValues?.freezeStartDate?.slice(0, 10)}
                        data-testid="freeze-end-date"
                        subText={formErrors.freezeEndDate}
                        state={!formErrors.freezeEndDate ? InputStates.Default : InputStates.Error}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
            </RadioCard>
          )}
        </div>
      </Grid>
    </Container>
  );
};
